import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ScrollView } from '@rbilabs/universal-components';

import {
  ModalPayloadType,
  OnShowModalType,
} from 'components/features/components/braze-marketing-card/types';
import { Container as MarketingCardsContainer } from 'components/features/components/marketing-card-group/styled';
import linkSerializers from 'components/features/components/terms/terms-link';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import DrawerPage from 'components/layout/drawer-page';
import { MarketingTileSkeleton } from 'components/marketing-tile/marketing-tile.skeleton';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { useNotificationCenterMarkAsReadMutation } from 'generated/graphql-gateway';
import useDialogModal from 'hooks/use-dialog-modal';
import { useAuthContext } from 'state/auth';
import { BrazeContext } from 'state/braze';
import { useNotificationCenterReadState } from 'state/braze/hooks/use-notification-center-read-state';
import { useNotificationCenterSession } from 'state/braze/hooks/use-notification-center-session';
import {
  filterContentCardsByServiceMode,
  getContentCardNotificationId,
  setReadContentCardsInLocalStorage,
} from 'state/braze/hooks/utils';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useServiceModeLive } from 'state/service-mode';
import Braze, { ContentCard } from 'utils/braze';
import logger from 'utils/logger';

import { CustomContentCard } from './content-cards';
import {
  CenteredLine,
  EmptyInboxCaption,
  EmptyInboxTitle,
  StyledPicture,
  UnreadSeparator,
  UnreadSeparatorText,
} from './styled';

export const NotificationInbox = () => {
  const dispatch = useAppDispatch();
  const contentCards = useAppSelector(selectors.loyalty.selectNewsFeedContentCards);
  const { isReady, readNotificationIds } = useNotificationCenterReadState();
  const { sessionId } = useNotificationCenterSession();
  const { isAuthenticated } = useAuthContext();
  const serviceMode = useServiceModeLive();
  const [unreadContentCardsIds, setUnreadContentCardsIds] = useState<string[]>([]);
  const [pendingTermsData, setPendingTermsData] = useState<ModalPayloadType>({
    content: [],
    heading: '',
  });
  const { logContentCardImpression } = useContext(BrazeContext);
  const [markNotificationsAsRead] = useNotificationCenterMarkAsReadMutation();
  // Ids whose mark-as-read mutation is in flight, to avoid resubmitting on an effect re-run.
  // Reset on session change below (the component can stay mounted across an account swap).
  const pendingMarkAsReadIdsRef = useRef<Set<string>>(new Set());
  // Card ids already logged as a Braze impression this session, so re-runs don't re-log them.
  const impressedCardIdsRef = useRef<Set<string>>(new Set());
  // Session whose authoritative read set already initialized unreadContentCardsIds: the first
  // ready run per session replaces it outright, later runs only add to it (so reading a card
  // mid-session doesn't make it visually jump out of the unread section).
  const initializedSessionIdRef = useRef<number | null>(null);
  const [TermsModal, openTermsModal] = useDialogModal({
    modalAppearanceEventMessage: 'Terms and Conditions',
  });

  const onShowTermsModal: OnShowModalType = data => {
    openTermsModal(data);
    setPendingTermsData(data);
  };

  const visibleContentCards = useMemo(
    () => filterContentCardsByServiceMode(contentCards, serviceMode),
    [contentCards, serviceMode]
  );

  // Control cards are blank by design and only ride along for their impression, so they take no
  // part in what the user actually sees: no read/unread classification, no mark-as-read, and a
  // feed holding nothing but control cards is still an empty inbox.
  const displayableContentCards = useMemo(
    () => visibleContentCards.filter(({ isControl }) => !isControl),
    [visibleContentCards]
  );

  // Runs before the effects below in the same commit (hooks run in declaration order), so a
  // session change clears stale in-flight/impression tracking before they process the new session.
  useEffect(() => {
    pendingMarkAsReadIdsRef.current = new Set();
    impressedCardIdsRef.current = new Set();
  }, [sessionId]);

  useEffect(() => {
    // Nothing is classified, mutated, or written to local storage until the read set is
    // authoritative - a card must never appear in the wrong section, even briefly.
    if (!isReady || displayableContentCards.length === 0) {
      return;
    }

    const unreadCards = displayableContentCards.filter(card => {
      const notificationId = getContentCardNotificationId(card);
      return notificationId !== null && !readNotificationIds.includes(notificationId);
    });

    const unreadCardsIds = unreadCards.map(({ id }) => id);

    if (initializedSessionIdRef.current !== sessionId) {
      initializedSessionIdRef.current = sessionId;
      setUnreadContentCardsIds(unreadCardsIds);
    } else if (unreadCardsIds.length > 0) {
      setUnreadContentCardsIds(prevState => [...new Set([...prevState, ...unreadCardsIds])]);
    }

    if (unreadCards.length === 0) {
      return;
    }

    const unreadNotificationIds = unreadCards
      .map(getContentCardNotificationId)
      .filter((id): id is string => id !== null);

    if (isAuthenticated) {
      const idsToSubmit = unreadNotificationIds.filter(
        id => !pendingMarkAsReadIdsRef.current.has(id)
      );

      if (idsToSubmit.length === 0) {
        return;
      }

      idsToSubmit.forEach(id => pendingMarkAsReadIdsRef.current.add(id));

      markNotificationsAsRead({ variables: { notificationIds: idsToSubmit } })
        .then(({ data }) => {
          // Resolves successfully even when the server reports failure (a Boolean, not a throw).
          if (!data?.notificationCenterMarkAsRead) {
            logger.warn({ message: 'Failed to mark notifications as read' });
            return;
          }

          dispatch(
            actions.loyalty.mergeServerReadNotificationIds({
              ids: idsToSubmit,
              sessionId,
            })
          );
          // Also remember on-device so the card survives this account logging out later.
          setReadContentCardsInLocalStorage(idsToSubmit);
        })
        .catch(error => {
          logger.warn({ error, message: 'Failed to mark notifications as read' });
        })
        .finally(() => {
          idsToSubmit.forEach(id => pendingMarkAsReadIdsRef.current.delete(id));
        });
    } else {
      setReadContentCardsInLocalStorage(unreadNotificationIds);
      dispatch(actions.loyalty.localReadContentCardsChanged());
    }
  }, [
    dispatch,
    displayableContentCards,
    isAuthenticated,
    isReady,
    markNotificationsAsRead,
    readNotificationIds,
    sessionId,
    unreadContentCardsIds.length,
  ]);

  // Control cards only: every card that renders logs its own impression (BrazeMarketingCard does
  // it once its image loads), so logging those here as well would double-count them. A control
  // card renders nothing, so the inbox has to log it - gated on isReady like the rendered cards,
  // to keep both arms of the A/B test counted at the same point. In its own effect so the
  // read/unread effect's unrelated re-runs don't re-log, and deduped per session by the ref.
  useEffect(() => {
    if (!isReady) {
      return;
    }

    visibleContentCards.forEach(({ id, isControl, viewed }) => {
      if (isControl && !viewed && !impressedCardIdsRef.current.has(id)) {
        impressedCardIdsRef.current.add(id);
        logContentCardImpression(id);
      }
    });
  }, [isReady, sessionId, visibleContentCards, logContentCardImpression]);

  const orderedContentCards = useMemo(
    () =>
      displayableContentCards.reduce(
        (acc, cur) => {
          const key = unreadContentCardsIds.includes(cur.id) ? 'unread' : 'read';
          acc[key].push(cur);
          return acc;
        },
        { read: [] as ContentCard[], unread: [] as ContentCard[] }
      ),
    [displayableContentCards, unreadContentCardsIds]
  );

  useEffect(() => {
    return () => Braze.requestContentCardsRefresh();
  }, []);

  return (
    <DrawerPage headerText={t('news')} dismissType="back" disableWebMaxWidth>
      <TermsModal
        heading={pendingTermsData.heading}
        bodyComponent={
          <ScrollView showsVerticalScrollIndicator={false}>
            <SanityBlockRenderer content={pendingTermsData.content} serializers={linkSerializers} />
          </ScrollView>
        }
      />
      {!isReady ? (
        // An empty displayableContentCards here isn't necessarily confirmed-empty - it can also
        // mean not loaded/confirmed yet. Same skeleton as homepage (2-column Captioned sizing).
        <CenteredRowContainer accessibilityRole="progressbar">
          <MarketingTileSkeleton />
        </CenteredRowContainer>
      ) : displayableContentCards.length === 0 ? (
        <>
          <StyledPicture source={require('./empty-state-image.png')} />
          <EmptyInboxTitle>{t('emptyInboxTitle')}</EmptyInboxTitle>
          <EmptyInboxCaption>{t('emptyInboxCaption')}</EmptyInboxCaption>
        </>
      ) : (
        <CenteredRowContainer>
          <MarketingCardsContainer testID="notification-inbox-unread-grid">
            {orderedContentCards.unread.map((card, index) => (
              <CustomContentCard
                key={card.id}
                card={card}
                index={index}
                isUnread
                onShowTermsModal={onShowTermsModal}
              />
            ))}
          </MarketingCardsContainer>
          <UnreadSeparator>
            <CenteredLine />
            <UnreadSeparatorText>{t('contentCardsSeparatorLabel')}</UnreadSeparatorText>
            <CenteredLine />
          </UnreadSeparator>
          <MarketingCardsContainer testID="notification-inbox-read-grid">
            {orderedContentCards.read.map((card, index) => (
              <CustomContentCard
                key={card.id}
                card={card}
                index={orderedContentCards.unread.length + index}
                isUnread={false}
                onShowTermsModal={onShowTermsModal}
              />
            ))}
          </MarketingCardsContainer>
        </CenteredRowContainer>
      )}
    </DrawerPage>
  );
};
